/**
 * LobbyView — the LOBBY status branch of GameScreen (docs/setup-redesign,
 * sections 4 and 5).
 *
 * One layout for every viewer, with controls by role (lib/lobbySeats.ts):
 *   host     — seat actions (Colour on their own seat, ✕ remove, Resend · ✕ on
 *              invites, Invite and Add bot on the first open seat), "Fill with
 *              bots", "Players: N ▾", the Invite card, Start / Delete.
 *   member   — own seat highlighted with Colour; start status; Leave game.
 *   invited  — banner, their saved seat marked; Decline + Join game.
 *   visitor  — "Take this seat" on every open seat (joins immediately).
 *
 * Desktop (≥1024): seats + invite in the main column, rules card and the start
 * card in a right rail. Tablet: rules, seats in two columns, invite, pinned
 * footer. Phone: rules, seats in one column, small share card, pinned footer.
 *
 * GameScreen owns the game-level mutations (start, join, leave, delete,
 * colour, decline, remove, revoke) and passes them in; lobby-only host tools
 * (bots, seat count, resend) live in their own hooks here.
 */

import React from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  useWindowDimensions,
} from 'react-native'
import { teamMarkColor } from '../../../lib/teamColors'
import { teamForSlot, teamLabel, teamsConfigOf } from '@warcarp/api-types'
import { useRouter } from 'expo-router'
import { AppModal } from '../../ui/AppModal'
import { Button } from '../../ui/Button'
import { SlotPicker } from '../SlotPicker'
import { LobbyPopup } from '../lobby/LobbyPopup'
import { playerColorSlot } from '../../../lib/playerColor'
import { getStartGameWarning } from '../../../lib/startGameWarning'
import { getDeclineInviteWarning } from '../../../lib/declineInviteWarning'
import type { GameResponse, GamePlayerResponse } from '@warcarp/api-types'
import { pageLayout } from '../../../lib/pageLayout'
import { useMap } from '../../../hooks/useMaps'
import { useResendInvite, useUpdateGameSettings, useUpdateMySlot } from '../../../hooks/useGames'
import { useLobbyBots } from '../../../hooks/useLobbyBots'
import { playersRuleValue } from '../../../lib/gameRulesSummary'
import { teamsLobbyLabel } from '../../../lib/teamSetup'
import { useTheme } from '../../../hooks/useTheme'
import { TeamShape } from '../TeamShape'
import {
  lobbyHostName,
  lobbyRole,
  lobbySeatRows,
  lobbyTeamGroups,
  lobbyStartStatus,
  maxPlayersChoices,
  startHint,
  type LobbySeatRow as SeatRowModel,
} from '../../../lib/lobbySeats'
import { LobbyRulesCard } from '../lobby/LobbyRulesCard'
import { LobbySeatRow, MiniAction } from '../lobby/LobbySeatRow'
import { LobbyInviteCard, LobbyShareCard } from '../lobby/LobbyInviteCard'
import { BotPickerModal } from '../lobby/BotPickerModal'
import { useToast } from '../../ui/Toast'

/** Tablet lobby: one centred column this wide. */
export const LOBBY_MAX_WIDTH = 760
/** Desktop lobby: main column + rail, capped this wide. */
export const LOBBY_DESKTOP_MAX_WIDTH = 1120
/** Desktop right rail (rules card + start card). */
export const LOBBY_RAIL_WIDTH = 300
const GAP = 12


export interface LobbyViewProps {
  game: GameResponse
  myPlayer: GamePlayerResponse | null | undefined
  viewerId: string | null | undefined
  viewerIsPlayer: boolean
  isAdmin: boolean
  mapDisplayName: string | null
  /** Take a seat: an open seat's slot, or undefined for the invitee's saved seat. */
  onTakeSeat: (slot: number | undefined) => void
  /** Slot being taken (undefined = the invitee's seat), null when idle. */
  takeSeatPending: { slot: number | undefined } | null
  onLeave: () => void
  leaveIsPending: boolean
  onDelete: () => void
  deleteIsPending: boolean
  onStart: () => void
  startIsPending: boolean
  onRevokeInvitation: (invitationId: string) => void
  revokeIsPending: boolean
  /** Viewer holds a pending invite — shows the Decline button. */
  viewerHasPendingInvite: boolean
  onDeclineInvite: () => void
  declineIsPending: boolean
  onRemovePlayer: (playerId: string) => void
  removeIsPending: boolean
  // Color picker
  colorPickerOpen: boolean
  onOpenColorPicker: () => void
  onCloseColorPicker: () => void
  onUpdateMyColor: (color: number) => void
  updateMyColorIsPending: boolean
  // Delete confirm modal
  deleteConfirmOpen: boolean
  onConfirmDelete: () => void
  onCancelDelete: () => void
}

export function LobbyView({
  game,
  myPlayer,
  viewerId,
  viewerIsPlayer,
  isAdmin,
  mapDisplayName,
  onTakeSeat,
  takeSeatPending,
  onLeave,
  leaveIsPending,
  onDelete,
  deleteIsPending,
  onStart,
  startIsPending,
  onRevokeInvitation,
  revokeIsPending,
  viewerHasPendingInvite,
  onDeclineInvite,
  declineIsPending,
  onRemovePlayer,
  removeIsPending,
  colorPickerOpen,
  onOpenColorPicker,
  onCloseColorPicker,
  onUpdateMyColor,
  updateMyColorIsPending: _updateMyColorIsPending,
  deleteConfirmOpen,
  onConfirmDelete,
  onCancelDelete,
}: LobbyViewProps) {
  const router = useRouter()
  const { showToast } = useToast()
  const { width: windowWidth } = useWindowDimensions()
  const desktop = pageLayout(windowWidth).tier === 'desktop'
  const { tier, inset, contentWidth } = pageLayout(windowWidth, { maxWidth: desktop ? LOBBY_DESKTOP_MAX_WIDTH : LOBBY_MAX_WIDTH })
  const phone = tier === 'phone'
  const { data: lobbyMap } = useMap(game.mapId ?? undefined)
  const bots = useLobbyBots(game)
  const updateSettings = useUpdateGameSettings(game.id)
  const resendInvite = useResendInvite(game.id)
  const updateMySlot = useUpdateMySlot(game.id)
  // "Move here" failures (seat just taken, game started) show inline under the seats.
  const [moveError, setMoveError] = React.useState<string | null>(null)

  const [startConfirmOpen, setStartConfirmOpen] = React.useState(false)
  const [declineConfirmOpen, setDeclineConfirmOpen] = React.useState(false)
  const [inviteSheetOpen, setInviteSheetOpen] = React.useState(false)
  const [botPickerOpen, setBotPickerOpen] = React.useState(false)
  // Pick-seats team lobbies: the seat the picker's bot takes (null: lowest open seat).
  const [botSeat, setBotSeat] = React.useState<number | null>(null)
  const [playersPickerOpen, setPlayersPickerOpen] = React.useState(false)

  const startWarning = getStartGameWarning(game)
  const declineWarning = getDeclineInviteWarning()
  // The role comes from the game itself; isAdmin/viewerIsPlayer from GameScreen agree with it.
  const role = lobbyRole(game, viewerId)
  const host = isAdmin && role === 'host'
  // Team games (docs/setup-redesign/teams-config.md "Lobby"): they start when
  // every seat is filled (no Start button), and with Pick seats the seats are
  // grouped by team, each open seat with its own Add bot.
  const teams = teamsConfigOf(game.setupConfig)
  // Team headers carry the team's shape, in the header's olive.
  const { theme } = useTheme()
  const teamMark = teamMarkColor(theme, false)
  const pickSeats = teams?.assignment === 'manual' ? teams : undefined
  const seats = lobbySeatRows(game, { viewerId, teams })
  const openSeats = seats.filter((s) => s.kind === 'open').length
  const startStatus = lobbyStartStatus(game, host)
  const hostName = lobbyHostName(game)
  const canStart = game.players.length >= game.minPlayers
  const seatChoices = host && !teams ? maxPlayersChoices(game, lobbyMap) : []
  const myTeam = pickSeats && myPlayer ? teamForSlot(myPlayer.slot, pickSeats.size) : null

  React.useEffect(() => {
    if (bots.actionError) showToast(bots.actionError, 'error')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bots.actionError])

  function requestStart() {
    if (startWarning) {
      setStartConfirmOpen(true)
      return
    }
    onStart()
  }

  function confirmStart() {
    setStartConfirmOpen(false)
    onStart()
  }

  function requestDecline() {
    setDeclineConfirmOpen(true)
  }

  function confirmDecline() {
    setDeclineConfirmOpen(false)
    onDeclineInvite()
  }

  function resend(userId: string, name: string) {
    resendInvite.mutate(userId, {
      onSuccess: () => showToast(`Invite sent to ${name} again`, 'success'),
      onError: (err) => showToast(err instanceof Error ? err.message : 'Could not resend the invite', 'error'),
    })
  }

  function moveTo(slot: number) {
    setMoveError(null)
    updateMySlot.mutate({ slot }, {
      onError: (err) => setMoveError(err instanceof Error ? err.message : 'Could not move to that seat'),
    })
  }

  function setMaxPlayers(maxPlayers: number) {
    setPlayersPickerOpen(false)
    if (maxPlayers === game.maxPlayers) return
    updateSettings.mutate({ maxPlayers }, {
      onError: (err) => showToast(err instanceof Error ? err.message : 'Could not change the seat count', 'error'),
    })
  }

  // ── Seats ────────────────────────────────────────────────────────────────
  function seatActions(seat: SeatRowModel) {
    return seat.actions.map((action) => {
      switch (action) {
        case 'colour':
          return <MiniAction key={action} label="Color" accessibilityLabel="Change your color" onPress={onOpenColorPicker} />
        case 'remove':
          return seat.kind === 'player' ? (
            <MiniAction key={action} label="✕" tone="quiet" accessibilityLabel={`Remove ${seat.name}`}
              onPress={() => onRemovePlayer(seat.player.id)} disabled={removeIsPending} />
          ) : null
        case 'resend':
          return seat.kind === 'invited' ? (
            <MiniAction key={action} label="Resend" tone="quiet" accessibilityLabel={`Resend invitation to ${seat.name}`}
              onPress={() => resend(seat.inviteeId, seat.name)} loading={resendInvite.isPending && resendInvite.variables === seat.inviteeId} />
          ) : null
        case 'revoke':
          return seat.kind === 'invited' ? (
            <MiniAction key={action} label="✕" tone="quiet" accessibilityLabel={`Revoke invitation for ${seat.name}`}
              onPress={() => onRevokeInvitation(seat.invitationId)} disabled={revokeIsPending} />
          ) : null
        case 'invite':
          return <MiniAction key={action} label="Invite" accessibilityLabel="Invite a player" onPress={() => setInviteSheetOpen(true)} />
        case 'addBot': {
          if (pickSeats) {
            const team = teamForSlot(seat.slot, pickSeats.size)
            return (
              <MiniAction key={action} label="Add bot ▾" accessibilityLabel={`Add a bot to seat ${seat.slot}${team ? ` on ${teamLabel(team)}` : ''}`}
                testID={`add-bot-seat-${seat.slot}`} onPress={() => { setBotSeat(seat.slot); setBotPickerOpen(true) }} disabled={bots.isFilling} />
            )
          }
          return (
            <MiniAction key={action} label="Add bot ▾" accessibilityLabel="Choose bot players"
              onPress={() => { setBotSeat(null); setBotPickerOpen(true) }} disabled={bots.isFilling} />
          )
        }
        case 'move': {
          const team = pickSeats ? teamForSlot(seat.slot, pickSeats.size) : null
          const pending = updateMySlot.isPending && updateMySlot.variables?.slot === seat.slot
          return (
            <MiniAction key={action} label="Move here" accessibilityLabel={`Move to seat ${seat.slot}${team ? ` on ${teamLabel(team)}` : ''}`}
              testID={`move-seat-${seat.slot}`} onPress={() => moveTo(seat.slot)}
              loading={pending} disabled={updateMySlot.isPending && !pending} />
          )
        }
        case 'take': {
          const pending = takeSeatPending?.slot === seat.slot
          return (
            <MiniAction key={action} label="Take this seat" tone="primary" accessibilityLabel={`Take seat ${seat.slot}`}
              testID={`take-seat-${seat.slot}`} onPress={() => onTakeSeat(seat.slot)}
              loading={pending} disabled={!!takeSeatPending && !pending} />
          )
        }
      }
    })
  }

  /** The bot picker's seat line: "seat 3 on Team B". */
  function seatPickerLabel(slot: number): string {
    const team = pickSeats ? teamForSlot(slot, pickSeats.size) : null
    return team ? `seat ${slot} on ${teamLabel(team)}` : `seat ${slot}`
  }

  // Pick seats: every seat belongs to a team, drawn in its shape. Random
  // teams have no team per seat before the start, so they keep circles.
  const seatTeam = (slot: number) => (pickSeats ? teamForSlot(slot, pickSeats.size) : undefined)

  function renderSeat(seat: SeatRowModel) {
    const actions = seatActions(seat)
    if (seat.kind === 'player') {
      return (
        <LobbySeatRow key={seat.slot} slot={seat.slot} kind="filled" name={seat.name} meta={seat.meta}
          colorSlot={playerColorSlot(seat.player)} team={seatTeam(seat.slot)} you={seat.you} vacation={seat.player.vacation ?? null}>
          {actions.length ? actions : null}
        </LobbySeatRow>
      )
    }
    return (
      <LobbySeatRow key={seat.slot} slot={seat.slot} kind={seat.kind} name={seat.name} meta={seat.meta} team={seatTeam(seat.slot)} you={seat.you}>
        {actions.length ? actions : null}
      </LobbySeatRow>
    )
  }

  const columns = phone ? 1 : 2
  const mainWidth = desktop ? contentWidth - LOBBY_RAIL_WIDTH - 20 : contentWidth
  const seatWidth = columns === 1 ? undefined : Math.floor((mainWidth - GAP * (columns - 1)) / columns)
  const declined = game.invitations.filter((inv) => inv.status === 'declined')

  const seatsSection = (
    <View style={{ gap: 12 }}>
      {/* Team lobbies drop the "Seats · n/m" header: the start status already
          shows the count, and a team lobby has no seat-count action. The team
          format chip, with Fill with bots, sits right above the teams. */}
      {!teams && <View className="flex-row items-center flex-wrap" style={{ gap: 8 }}>
        <Text className="text-ink-soft text-xs font-stencil uppercase tracking-widest" style={{ flexGrow: 1 }}>
          {phone ? `Seats · ${game.players.length}/${game.maxPlayers}` : `Seats · ${game.players.length} of ${game.maxPlayers}`}
        </Text>
        {host && openSeats > 0 && (
          <MiniAction label={bots.isFilling ? 'Filling…' : 'Fill with bots'} accessibilityLabel="Fill remaining seats with random bots"
            onPress={() => void bots.fillWithBots()} loading={bots.isFilling} disabled={bots.isPending} />
        )}
        {host && seatChoices.length > 1 && (
          <MiniAction label={`Players: ${game.maxPlayers} ▾`} accessibilityLabel={`Players: ${game.maxPlayers}. Change the number of seats`}
            onPress={() => setPlayersPickerOpen(true)} loading={updateSettings.isPending} />
        )}
      </View>}
      {host && bots.addedNote && <Text className="text-olive-up font-typed text-xs">✓ {bots.addedNote}</Text>}
      {teams && (
        <View testID="lobby-teams-label" className="flex-row items-center flex-wrap" style={{ gap: 8 }}>
          <View className="bg-olive-up rounded-full px-3 py-1">
            <Text className="text-canvas font-stencil text-[11px] uppercase tracking-wider">{teamsLobbyLabel(teams)}</Text>
          </View>
          {!pickSeats && <Text className="text-ink-soft font-typed text-xs" style={{ flexGrow: 1 }}>Teams are drawn when the game starts.</Text>}
          {pickSeats && <View style={{ flexGrow: 1 }} />}
          {host && openSeats > 0 && (
            <MiniAction label={bots.isFilling ? 'Filling…' : 'Fill with bots'} accessibilityLabel="Fill remaining seats with random bots"
              testID="lobby-fill-bots" onPress={() => void bots.fillWithBots()} loading={bots.isFilling} disabled={bots.isPending} />
          )}
        </View>
      )}
      {pickSeats ? (
        <View testID="lobby-seats" style={columns === 1 ? { gap: 14 } : { flexDirection: 'row', flexWrap: 'wrap', gap: GAP }}>
          {lobbyTeamGroups(seats, pickSeats).map((group) => (
            <View key={group.team} testID={`lobby-team-${group.team}`} accessibilityLabel={group.label}
              style={columns === 1 ? { gap: 8 } : { width: seatWidth, gap: 8 }}>
              <View className="flex-row items-center" style={{ gap: 6 }}>
                <TeamShape team={group.team} size={14} fill={teamMark} />
                <Text accessibilityRole="header" className="text-olive-up text-[11px] font-stencil uppercase tracking-widest">{group.label}</Text>
              </View>
              {group.rows.map(renderSeat)}
            </View>
          ))}
        </View>
      ) : (
        <View testID="lobby-seats" style={columns === 1 ? { gap: 8 } : { flexDirection: 'row', flexWrap: 'wrap', gap: GAP }}>
          {seats.map((seat) => (columns === 1 ? renderSeat(seat) : (
            <View key={seat.slot} style={{ width: seatWidth }}>{renderSeat(seat)}</View>
          )))}
        </View>
      )}
      {moveError && <Text testID="lobby-move-error" accessibilityRole="alert" className="text-blood font-typed text-xs">{moveError}</Text>}
      {/* Declined invites: their seats are already open above; this line tells
          the host who turned the invite down (declining is final). */}
      {declined.length > 0 && (
        <Text className="text-ink-soft font-typed text-xs italic" accessibilityLabel="Declined invitations">
          Declined: {declined.map((inv) => inv.user.displayName).join(', ')}
        </Text>
      )}
      {role === 'visitor' && openSeats === 0 && (
        <Text className="text-ink-soft font-typed text-xs italic">This game is full. No open seats.</Text>
      )}
    </View>
  )

  const rulesCard = (
    <LobbyRulesCard
      mapId={game.mapId}
      mapName={mapDisplayName}
      setupConfig={game.setupConfig}
      playersValue={playersRuleValue(game)}
      maxPlayers={game.maxPlayers}
      bootAfterSeconds={game.bootAfterSeconds}
      onEdit={host ? () => router.push(`/(app)/games/${game.id}/setup`) : undefined}
    />
  )

  const invitedBanner = role === 'invited' ? (
    <View testID="lobby-invited-banner" className="bg-paper-up border border-olive-up rounded-xl px-3 py-2.5">
      <Text className="text-ink font-typed text-sm">
        <Text className="font-stencil">{hostName} invited you.</Text> Your seat is saved until you answer.
      </Text>
    </View>
  ) : null

  // Phone hosts get the small share card; tablet/desktop hosts the full Invite card.
  const shareSection = host
    ? (phone ? <LobbyShareCard game={game} isHost /> : <LobbyInviteCard game={game} seatsOpen={openSeats} />)
    : viewerIsPlayer && game.isPublic ? <LobbyShareCard game={game} isHost={false} /> : null

  // ── Start / leave / join actions ─────────────────────────────────────────
  // Auto-start games: the host can still start early ("Start now"); the
  // start warning confirms because seats are still open.
  const startButton = (
    <Button
      title={startStatus.kind === 'auto' ? 'Start now' : 'Start game'}
      textColor="#fff"
      onPress={requestStart}
      loading={startIsPending}
      disabled={!canStart}
      accessibilityLabel="Start the game"
    />
  )
  const deleteButton = (
    <Button title={phone ? 'Delete' : 'Delete game'} variant="secondary" onPress={onDelete} loading={deleteIsPending} accessibilityLabel="Delete this game" />
  )
  const leaveButton = (
    <Button title="Leave game" variant="secondary" onPress={onLeave} loading={leaveIsPending} accessibilityLabel="Leave this game" />
  )
  const declineButton = (
    <Button title="Decline" variant="secondary" onPress={requestDecline} loading={declineIsPending} accessibilityLabel="Decline this game invitation" />
  )
  const joinButton = (
    <Button
      title="Join game"
      textColor="#fff"
      onPress={() => onTakeSeat(undefined)}
      loading={takeSeatPending != null && takeSeatPending.slot === undefined}
      accessibilityLabel="Join game"
    />
  )

  const manualHost = role === 'host' && startStatus.kind === 'manual-host'
  /** The status text above the actions, or null. */
  const statusText: string | null =
    role === 'host'
      ? startStatus.kind === 'auto' ? startStatus.text : startHint(game)
      : role === 'member'
        ? startStatus.kind === 'manual-host' ? null : startStatus.text
        : role === 'visitor'
          ? openSeats > 0 ? 'Taking a seat joins the game.' : null
          : null

  /** [secondary, primary] actions for the role. */
  // Team games start on their own when the last seat fills: no Start button.
  const actionPair: [React.ReactNode, React.ReactNode] =
    role === 'host'
      ? [deleteButton, teams ? null : startButton]
      : role === 'member'
        ? [leaveButton, null]
        : role === 'invited'
          ? [declineButton, joinButton]
          : [viewerHasPendingInvite ? declineButton : null, null]

  // Desktop rail: the status sits in a dashed box (auto-start / waiting) or,
  // for a manual-start host, as a hint under the Start button.
  const startCard = (
    <View testID="lobby-footer" className="bg-paper border border-olive rounded-xl p-3 gap-2.5">
      <Text className="text-ink-soft text-[10px] font-stencil uppercase tracking-widest">
        {role === 'invited' || role === 'visitor' ? 'Join' : 'Start'}
      </Text>
      {statusText && !manualHost && (
        <View className="border-2 border-dashed border-olive rounded-lg px-3 py-2.5">
          <Text testID="lobby-start-status" className="text-ink font-typed text-sm text-center">{statusText}</Text>
        </View>
      )}
      {actionPair[1]}
      {statusText && manualHost && (
        <Text testID="lobby-start-status" className="text-ink-soft font-typed text-xs text-center">{statusText}</Text>
      )}
      {actionPair[0]}
    </View>
  )

  const footer = (
    <View
      testID="lobby-footer"
      className="border-t border-olive bg-canvas gap-2"
      style={{ paddingHorizontal: inset, paddingTop: 10, paddingBottom: 16 }}
    >
      {statusText && (
        <Text testID="lobby-start-status" className={`font-typed text-center ${manualHost ? 'text-ink-soft text-xs' : 'text-ink text-sm'}`}>
          {statusText}
        </Text>
      )}
      {(actionPair[0] || actionPair[1]) && (
        <View className="flex-row" style={{ gap: 10 }}>
          {actionPair[0] && <View style={{ flex: 1 }}>{actionPair[0]}</View>}
          {actionPair[1] && <View style={{ flex: 2 }}>{actionPair[1]}</View>}
        </View>
      )}
    </View>
  )

  return (
    <>
      <ScrollView
        testID="lobby-content-scroll"
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingTop: 12, paddingBottom: 24, paddingHorizontal: inset }}
      >
        {desktop ? (
          <View className="flex-row" style={{ gap: 20, alignItems: 'flex-start' }}>
            <View testID="lobby-desktop-column" style={{ flex: 1, minWidth: 0, gap: 14 }}>
              {invitedBanner}
              {seatsSection}
              {shareSection}
            </View>
            <View testID="lobby-desktop-rail" style={{ width: LOBBY_RAIL_WIDTH, gap: 12 }}>
              {rulesCard}
              {startCard}
            </View>
          </View>
        ) : (
          <View style={{ gap: 14 }}>
            {invitedBanner}
            {rulesCard}
            {seatsSection}
            {shareSection}
          </View>
        )}
      </ScrollView>
      {!desktop && footer}

      {/* ── Host: invite sheet (seat "Invite") ─────────────────────────── */}
      {host && (
        <AppModal visible={inviteSheetOpen} animationType="fade" transparent onRequestClose={() => setInviteSheetOpen(false)}>
          <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
            <View className="bg-canvas rounded-2xl p-4 w-full gap-3" style={{ maxWidth: 480 }}>
              <LobbyInviteCard game={game} seatsOpen={openSeats} autoFocus />
              <Button title="Done" variant="secondary" onPress={() => setInviteSheetOpen(false)} accessibilityLabel="Close invite" />
            </View>
          </View>
        </AppModal>
      )}

      {/* ── Host: bot picker (seat "Add bot") ──────────────────────────── */}
      {host && (
        <BotPickerModal
          visible={botPickerOpen}
          onClose={() => setBotPickerOpen(false)}
          players={game.players}
          statuses={bots.statuses}
          onAdd={(profile) => {
            if (botSeat == null) { void bots.addBot(profile); return }
            // A chosen seat takes one bot: close once it is seated.
            setBotPickerOpen(false)
            void bots.addBot(profile, botSeat)
          }}
          busy={bots.isPending || bots.isFilling}
          seatsOpen={botSeat == null ? openSeats : Math.min(openSeats, 1)}
          {...(botSeat != null ? { seatLabel: seatPickerLabel(botSeat) } : {})}
        />
      )}

      {/* ── Host: seat count ("Players: N ▾") ──────────────────────────── */}
      {host && (
        <LobbyPopup visible={playersPickerOpen} title="Players" closeLabel="Close players picker" onClose={() => setPlayersPickerOpen(false)} testID="lobby-players-modal" maxWidth={380}>
          <Text className="font-typed text-ink-soft text-sm mb-3">
            Seats in this game. You can&apos;t go below the players already seated or invited, or above what the map holds.
          </Text>
          <View className="flex-row flex-wrap" style={{ gap: 8 }}>
            {seatChoices.map((n) => (
              <TouchableOpacity
                key={n}
                onPress={() => setMaxPlayers(n)}
                accessibilityLabel={`${n} players`}
                accessibilityRole="button"
                accessibilityState={{ selected: n === game.maxPlayers }}
                className={`rounded-lg border-2 items-center justify-center ${n === game.maxPlayers ? 'bg-olive border-olive-up' : 'bg-paper-up border-olive'}`}
                style={{ minWidth: 52, minHeight: 44, paddingHorizontal: 12 }}
              >
                <Text className={`font-stencil text-base ${n === game.maxPlayers ? 'text-paper' : 'text-ink'}`}>{n}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </LobbyPopup>
      )}

      {/* ── Start-game warning (LOBBY, host only) ──────────────────────── */}
      {isAdmin && startWarning && (
        <AppModal
          visible={startConfirmOpen}
          animationType="fade"
          transparent
          onRequestClose={() => setStartConfirmOpen(false)}
        >
          <View
            style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 24 }}
          >
            <View className="bg-canvas rounded-2xl p-6 w-full gap-4" style={{ maxWidth: 360 }}>
              <Text
                className="font-stencil text-ink text-lg uppercase"
                accessibilityRole="header"
                accessibilityLabel={startWarning.title}
              >
                {startWarning.title}
              </Text>
              <Text
                className="font-typed text-ink-soft text-sm"
                accessibilityLabel={startWarning.message}
              >
                {startWarning.message}
              </Text>
              <View className="flex-row gap-3 mt-1">
                <View className="flex-1">
                  <Button
                    title="Cancel"
                    variant="secondary"
                    onPress={() => setStartConfirmOpen(false)}
                    accessibilityLabel="Cancel start game"
                  />
                </View>
                <View className="flex-1">
                  <Button
                    title="Start Game"
                    onPress={confirmStart}
                    loading={startIsPending}
                    accessibilityLabel="Confirm start game"
                  />
                </View>
              </View>
            </View>
          </View>
        </AppModal>
      )}

      {/* ── Decline-invite confirm (LOBBY, invited viewer) ─────────────── */}
      {viewerHasPendingInvite && (
        <AppModal
          visible={declineConfirmOpen}
          animationType="fade"
          transparent
          onRequestClose={() => setDeclineConfirmOpen(false)}
        >
          <View
            style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 24 }}
          >
            <View className="bg-canvas rounded-2xl p-6 w-full gap-4" style={{ maxWidth: 360 }}>
              <Text
                className="font-stencil text-ink text-lg uppercase"
                accessibilityRole="header"
                accessibilityLabel={declineWarning.title}
              >
                {declineWarning.title}
              </Text>
              <Text
                className="font-typed text-ink-soft text-sm"
                accessibilityLabel={declineWarning.message}
              >
                {declineWarning.message}
              </Text>
              <View className="flex-row gap-3 mt-1">
                <View className="flex-1">
                  <Button
                    title="Cancel"
                    variant="secondary"
                    onPress={() => setDeclineConfirmOpen(false)}
                    accessibilityLabel="Cancel declining this game"
                  />
                </View>
                <View className="flex-1">
                  <Button
                    title="Decline"
                    onPress={confirmDecline}
                    loading={declineIsPending}
                    accessibilityLabel="Confirm declining this game"
                  />
                </View>
              </View>
            </View>
          </View>
        </AppModal>
      )}

      {/* ── Delete-game confirm (LOBBY, host only) ──────────────────────── */}
      {isAdmin && (
        <AppModal
          visible={deleteConfirmOpen}
          animationType="fade"
          transparent
          onRequestClose={onCancelDelete}
        >
          <View
            style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 24 }}
          >
            <View className="bg-canvas rounded-2xl p-6 w-full gap-4" style={{ maxWidth: 360 }}>
              <Text className="font-stencil text-ink text-lg uppercase">Delete this game?</Text>
              <Text className="font-typed text-ink-soft text-sm">
                The game and any pending invitations will be removed.
                Players who joined will see it disappear from their list.
                This can&apos;t be undone.
              </Text>
              <View className="flex-row gap-3 mt-1">
                <View className="flex-1">
                  <Button
                    title="Cancel"
                    variant="secondary"
                    onPress={onCancelDelete}
                  />
                </View>
                <View className="flex-1">
                  <Button
                    title="Delete"
                    onPress={onConfirmDelete}
                    loading={deleteIsPending}
                  />
                </View>
              </View>
            </View>
          </View>
        </AppModal>
      )}

      {/* ── Color picker (LOBBY): a small modal over the lobby. Tapping a
          free colour saves it and closes (handleUpdateMyColor); tapping your
          own, the ✕ or the backdrop just closes. ─────────────────────── */}
      {myPlayer && (
        <LobbyPopup visible={colorPickerOpen} title="Change color" closeLabel="Close color picker" onClose={onCloseColorPicker} testID="lobby-color-modal" maxWidth={460}>
          {/* Shared picker (same grid as settings). Colors held by OTHER
              players are taken. */}
          <SlotPicker
            label=""
            players={game.players.filter((p) => p.id !== myPlayer.id)}
            selectedColor={playerColorSlot(myPlayer)}
            selectedNote="current"
            team={myTeam ?? undefined}
            onSelect={(color) => {
              if (color === playerColorSlot(myPlayer)) onCloseColorPicker()
              else onUpdateMyColor(color)
            }}
          />
        </LobbyPopup>
      )}
    </>
  )
}
